import React, { useMemo, useState } from 'react';
import {
  FiActivity,
  FiAlertCircle,
  FiAward,
  FiChevronRight,
  FiHome,
  FiMapPin,
} from 'react-icons/fi';
import { EmptyState, GapNotice } from './states';
import { PMS_LABELS, fmtMoney, fmtNumber } from './derive';

const TABS = [
  { id: 'activity', label: 'Activity', icon: FiActivity },
  { id: 'leaders', label: 'Leaders', icon: FiAward },
  { id: 'anomalies', label: 'Anomalies', icon: FiAlertCircle },
];

const formatRelative = (date) => {
  if (!date) return '—';
  const ms = Date.now() - new Date(date).getTime();
  if (!Number.isFinite(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Right-rail panel with three tabs:
// • Activity   — collapsed feed of new hosts + new properties
// • Leaders    — top hosts, PMS, markets
// • Anomalies  — surfaces gap notices and error states inline
//
// Body region scrolls internally so the cockpit stays at viewport height.
export default function InsightsPanel({
  newHosts = [],
  newProperties = [],
  hostsLoading = false,
  propertiesAvailable = false,
  hostOrderStats,
  enrichedHosts = [],
  filteredHosts = [],
  filteredOrders = [],
  properties = [],
  hostsError,
  ordersError,
  onSelectHost,
  onSelectProperty,
  onSelectPms,
}) {
  const [tab, setTab] = useState('activity');

  const sortedNewHosts = useMemo(
    () =>
      [...newHosts]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 30),
    [newHosts]
  );

  const sortedNewProps = useMemo(
    () =>
      [...newProperties]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 30),
    [newProperties]
  );

  const topHosts = useMemo(() => {
    const rows = filteredHosts
      .map((h) => {
        const s = hostOrderStats?.get(String(h._id)) || { orders: 0, revenue: 0, aov: 0 };
        return { ...h, orders: s.orders, revenue: s.revenue, aov: s.aov };
      })
      .filter((r) => r.orders > 0)
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 5);
    return rows;
  }, [filteredHosts, hostOrderStats]);

  const topPms = useMemo(() => {
    const m = new Map();
    filteredHosts.forEach((h) => {
      const k = h.pmsType || 'unknown';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return Array.from(m.entries())
      .filter(([k]) => k !== 'unknown')
      .map(([id, count]) => ({ id, label: PMS_LABELS[id] || id, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  }, [filteredHosts]);

  const topMarkets = useMemo(() => {
    const m = new Map();
    properties.forEach((p) => {
      const c = p?.address?.city || '';
      if (!c) return;
      m.set(c, (m.get(c) || 0) + 1);
    });
    return Array.from(m.entries())
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  }, [properties]);

  const anomalies = useMemo(() => {
    const items = [];
    if (hostsError) {
      items.push({ id: 'hostsErr', severity: 'error', label: 'Hosts API error', detail: String(hostsError) });
    }
    if (ordersError) {
      items.push({ id: 'ordersErr', severity: 'error', label: 'Orders API error', detail: String(ordersError) });
    }
    if (!propertiesAvailable) {
      items.push({
        id: 'propsGap',
        severity: 'warning',
        label: 'Property timestamps unavailable',
        detail: 'Awaiting backend extension on /properties (propertySource + createdAt).',
      });
    }
    const unknownPms = enrichedHosts.filter((h) => !h.pmsType || h.pmsType === 'unknown').length;
    if (unknownPms > 0) {
      items.push({
        id: 'pmsUnknown',
        severity: 'warning',
        label: `${fmtNumber(unknownPms)} hosts with unknown PMS`,
        detail: 'Backend §A would expose pmsType directly on the /hosts list.',
      });
    }
    const stalledRequests = filteredOrders.filter((o) => o.status === 'requested').length;
    if (stalledRequests > 5) {
      items.push({
        id: 'stalled',
        severity: 'info',
        label: `${fmtNumber(stalledRequests)} orders in 'requested' state`,
        detail: 'Inspect via Orders → View records.',
      });
    }
    return items;
  }, [hostsError, ordersError, propertiesAvailable, enrichedHosts, filteredOrders]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex items-center border-b border-zinc-100 px-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          const badge =
            t.id === 'activity'
              ? sortedNewHosts.length + sortedNewProps.length
              : t.id === 'leaders'
              ? topHosts.length + topPms.length + topMarkets.length
              : anomalies.length;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              aria-pressed={active}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-2 py-2 text-[11.5px] font-medium transition-colors duration-150 motion-reduce:transition-none ${
                active
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-500 hover:text-zinc-800'
              }`}
            >
              <Icon className="h-3 w-3" />
              {t.label}
              {badge > 0 ? (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                    t.id === 'anomalies'
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-zinc-100 text-zinc-600'
                  }`}
                >
                  {badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        key={tab}
        className="flex-1 overflow-y-auto animate-[fadeInPanel_150ms_ease-out_both] motion-reduce:animate-none"
      >
        {tab === 'activity' ? (
          <ActivityList
            newHosts={sortedNewHosts}
            newProperties={sortedNewProps}
            hostsLoading={hostsLoading}
            propertiesAvailable={propertiesAvailable}
            onSelectHost={onSelectHost}
            onSelectProperty={onSelectProperty}
          />
        ) : tab === 'leaders' ? (
          <LeadersList
            topHosts={topHosts}
            topPms={topPms}
            topMarkets={topMarkets}
            onSelectHost={onSelectHost}
            onSelectPms={onSelectPms}
          />
        ) : (
          <AnomalyList items={anomalies} />
        )}
      </div>

      <style>{`
        @keyframes fadeInPanel {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ActivityList({ newHosts, newProperties, hostsLoading, propertiesAvailable, onSelectHost, onSelectProperty }) {
  if (hostsLoading && newHosts.length === 0 && newProperties.length === 0) {
    return (
      <div className="space-y-2 p-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-11 animate-pulse rounded-lg bg-zinc-100" />
        ))}
      </div>
    );
  }
  if (newHosts.length === 0 && newProperties.length === 0) {
    return (
      <EmptyState title="No recent activity" description="Try widening the date range." icon={FiActivity} />
    );
  }
  return (
    <div className="divide-y divide-zinc-100">
      {newHosts.length > 0 ? (
        <Section title="New hosts" count={newHosts.length}>
          <ul>
            {newHosts.map((h) => (
              <li key={`h-${h._id}`}>
                <button
                  type="button"
                  onClick={() => onSelectHost?.(h)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left transition-colors duration-100 hover:bg-zinc-50 motion-reduce:transition-none"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-[10px] font-semibold text-emerald-700">
                    {(h.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate text-[12px] font-semibold text-zinc-900">{h.name}</p>
                      {h.pmsType && h.pmsType !== 'unknown' ? (
                        <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-indigo-700">
                          {PMS_LABELS[h.pmsType] || h.pmsType}
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {h.email || h.phoneNumber || '—'} · {h.propertyCount || 0} props
                    </p>
                  </div>
                  <span className="text-[10px] tabular-nums text-zinc-400 group-hover:text-zinc-600">
                    {formatRelative(h.createdAt)}
                  </span>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {propertiesAvailable && newProperties.length > 0 ? (
        <Section title="New properties" count={newProperties.length}>
          <ul>
            {newProperties.map((p) => (
              <li key={`p-${p._id}`}>
                <button
                  type="button"
                  onClick={() => onSelectProperty?.(p)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left transition-colors duration-100 hover:bg-zinc-50 motion-reduce:transition-none"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
                    <FiHome className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">
                      {p?.address?.line1 || p?.listingTitle || 'Property'}
                    </p>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {p?.address?.city || ''}{p?.address?.state ? `, ${p.address.state}` : ''}
                      {p.propertySource ? ` · ${PMS_LABELS[p.propertySource] || p.propertySource}` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] tabular-nums text-zinc-400 group-hover:text-zinc-600">
                    {formatRelative(p.createdAt)}
                  </span>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {!propertiesAvailable ? (
        <Section title="New properties" right={<GapNotice label="Awaiting backend §B" />}>
          <p className="px-3 py-3 text-[11px] text-zinc-500">
            New property stream lights up once backend exposes <code>createdAt</code>.
          </p>
        </Section>
      ) : null}
    </div>
  );
}

function LeadersList({ topHosts, topPms, topMarkets, onSelectHost, onSelectPms }) {
  if (topHosts.length === 0 && topPms.length === 0 && topMarkets.length === 0) {
    return (
      <EmptyState title="No leaders yet" description="Once orders land, top hosts will surface here." icon={FiAward} />
    );
  }
  return (
    <div className="divide-y divide-zinc-100">
      <Section title="Top hosts by orders" count={topHosts.length}>
        {topHosts.length === 0 ? (
          <p className="px-3 py-3 text-[11px] text-zinc-500">No host orders in scope.</p>
        ) : (
          <ul>
            {topHosts.map((h, idx) => (
              <li key={h._id}>
                <button
                  type="button"
                  onClick={() => onSelectHost?.(h)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">{h.name}</p>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {fmtNumber(h.orders)} orders · {fmtMoney(h.revenue)}
                    </p>
                  </div>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Top PMS" count={topPms.length}>
        {topPms.length === 0 ? (
          <p className="px-3 py-3 text-[11px] text-zinc-500">No PMS signal yet.</p>
        ) : (
          <ul>
            {topPms.map((p, idx) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onSelectPms?.(p.id)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">{p.label}</p>
                    <p className="truncate text-[10.5px] text-zinc-500">{fmtNumber(p.count)} hosts</p>
                  </div>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Top markets" count={topMarkets.length}>
        {topMarkets.length === 0 ? (
          <p className="px-3 py-3 text-[11px] text-zinc-500">No city data — backend extension required.</p>
        ) : (
          <ul>
            {topMarkets.map((m, idx) => (
              <li key={m.city}>
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">
                      <FiMapPin className="-mt-0.5 mr-1 inline h-3 w-3 text-zinc-400" />
                      {m.city}
                    </p>
                    <p className="truncate text-[10.5px] text-zinc-500">{fmtNumber(m.count)} properties</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function AnomalyList({ items }) {
  if (items.length === 0) {
    return (
      <div className="px-3 py-6">
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-3 text-center text-[11.5px] text-emerald-800">
          All systems nominal · no anomalies detected
        </div>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-zinc-100">
      {items.map((it) => {
        const tone =
          it.severity === 'error'
            ? 'bg-red-50 text-red-700 ring-red-200'
            : it.severity === 'warning'
            ? 'bg-amber-50 text-amber-700 ring-amber-200'
            : 'bg-sky-50 text-sky-700 ring-sky-200';
        return (
          <li key={it.id} className="px-3 py-2.5">
            <div className="flex items-start gap-2">
              <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md ring-1 ${tone}`}>
                <FiAlertCircle className="h-3 w-3" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-zinc-900">{it.label}</p>
                {it.detail ? <p className="mt-0.5 text-[11px] text-zinc-500">{it.detail}</p> : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Section({ title, count, right, children }) {
  return (
    <div>
      <div className="flex items-center justify-between bg-zinc-50/60 px-3 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{title}</span>
        <div className="flex items-center gap-1.5">
          {typeof count === 'number' && count > 0 ? (
            <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[9px] font-semibold text-zinc-600">
              {count}
            </span>
          ) : null}
          {right}
        </div>
      </div>
      {children}
    </div>
  );
}
